import { useState, useCallback } from 'react';
import { X, Minus, Maximize2, GripHorizontal, BookOpen } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { useDraggablePanel } from '@/hooks/useDraggablePanel';
import type { WorkflowState } from '@/types/workflow';
import { buildAiSystemPrompt } from '@/lib/cartographer/aiPrompt';
import { CopyButton } from '@/components/ai/CopyButton';
import { AiPromptTab } from '@/components/ai/AiPromptTab';
import { ExamplesTab } from '@/components/ai/ExamplesTab';
import { HowToTab } from '@/components/ai/HowToTab';
import { JsonSchemaTab } from '@/components/ai/JsonSchemaTab';

/**
 * Minimal demonstrative example for the AI JSON documentation panel.
 * NOT auto-loaded to the canvas — just a reference shape for prompt-writers.
 */
const AI_EXAMPLE_WORKFLOW: WorkflowState = {
  name: 'Order Processing',
  coordinateOrigin: 'center',
  blocks: [
    { id: 'b1', type: 'general', x: -200, y: 0, color: '#22c55e',
      config: { name: 'Receive Order', description: 'Incoming order\nfrom web or API', icon: 'inbox' } },
    { id: 'b2', type: 'condition', x: 0, y: 0, color: '#f59e0b',
      config: {
        name: 'In Stock?',
        description: 'Check inventory\nreject if missing',
        // detailedDescription = lång markdown som visas i Detaljer-
        // dialogen. Emit:as bara när blocket faktiskt har dokumentation
        // värd att läsa (runbook, schema, decision-rationale).
        detailedDescription: '## Inventory check\n\nQueries `inventory.stock_level` for each line-item.\n\n- ≥ requested → branch **yes** (downstream confirm).\n- < requested → branch **no** (reject + notify).\n\nTimeout: 5s. Falls back to `no` on connection failure.',
        icon: 'git-branch',
      } },
    { id: 'b3', type: 'send-email', x: 200, y: -60, color: '#6366f1',
      config: { name: 'Confirm', description: 'Send receipt', icon: 'mail' } },
    { id: 'b4', type: 'send-email', x: 200, y: 60, color: '#ef4444',
      config: { name: 'Reject', description: 'Out-of-stock notice', icon: 'mail-x' } },
  ],
  connectors: [
    // Standard connector.
    { id: 'c1', fromId: 'b1', toId: 'b2', fromPort: 'right', toPort: 'left' },
    // Pulsing connector — animated dashed flow, custom color.
    { id: 'c2', fromId: 'b2', toId: 'b3', fromPort: 'right', toPort: 'left', label: 'yes', pulse: true, pulseColor: '#22c55e' },
    // Bidirectional connector — double-headed arrow.
    { id: 'c3', fromId: 'b2', toId: 'b4', fromPort: 'right', toPort: 'left', label: 'no', bidirectional: true },
  ],
  groups: [],
};

// ENHANCED_PROMPT is now built from the shared cartographer/spec.ts
// so the palette, detail-levels, layout grid, schema-rules, and
// operating procedure stay in sync with the deterministic engine
// (repoToWorkflow) and the parity test (cartographerSpecParity).
const ENHANCED_PROMPT = buildAiSystemPrompt();


const MULTI_LAYER_EXAMPLE = JSON.stringify(AI_EXAMPLE_WORKFLOW, null, 2);


interface AiJsonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Plan outline" tab content — surfaces the deterministic plan outline
 * the upload-repo engine produces, so users can copy it as a reference
 * before asking an LLM to author a similar diagram. Empty when no zip
 * has been uploaded in the current session.
 */
function PlanOutlineTabContent() {
  const [outline, setOutline] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('fw.repo.lastPlanOutline');
    } catch {
      return null;
    }
  });

  const refresh = useCallback(() => {
    try {
      setOutline(sessionStorage.getItem('fw.repo.lastPlanOutline'));
    } catch {
      setOutline(null);
    }
  }, []);

  if (!outline) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Plan outline</h3>
        <p className="text-muted-foreground text-sm">
          When you upload a <code className="text-[11px]">.zip</code> via{' '}
          <strong>Workflow → Upload repository</strong>, the cartographer engine produces a plain-text
          plan (SUBSYSTEM → GROUPS → KEY BLOCKS) and surfaces it here. You can then copy it as a
          reference for an external LLM, or use it to spot-check what was bucketed where.
        </p>
        <div className="p-3 rounded-lg bg-muted text-xs text-muted-foreground">
          No plan outline yet. Upload a repository to populate this tab.
        </div>
        <Button variant="outline" size="sm" onClick={refresh}>Refresh</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Plan outline (most recent upload)</h3>
        <CopyButton text={outline} label="Copy outline" />
      </div>
      <p className="text-muted-foreground text-sm">
        Paste this as a reference into an LLM chat — alongside the AI prompt from the previous tab —
        when you want the model to author a similar structure for a different repository.
      </p>
      <pre className="p-4 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap break-words max-h-[420px] overflow-y-auto border border-border">
        {outline}
      </pre>
      <Button variant="ghost" size="sm" onClick={refresh}>Refresh from latest upload</Button>
    </div>
  );
}

export function AiJsonDialog({ open, onOpenChange }: AiJsonDialogProps) {
  const { position, folded, setFolded, panelRef, dragHandlers } = useDraggablePanel({
    open,
    desktopInitialPosition: { x: 60, y: 80 },
  });

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      style={{ left: position.x, top: position.y }}
      className="fixed z-50 shadow-2xl rounded-lg border border-border bg-background flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Title bar – draggable */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-muted rounded-t-lg cursor-grab active:cursor-grabbing select-none border-b border-border"
        {...dragHandlers}
      >
        <div className="flex items-center gap-2">
          <GripHorizontal className="w-4 h-4 text-muted-foreground" />
          <BookOpen className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold truncate">AI & JSON Reference</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setFolded(!folded)}>
            {folded ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onOpenChange(false)}>
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Content – collapsible */}
      {!folded && (
        <div className="w-[min(90vw,640px)] max-h-[60vh] flex flex-col">
          <Tabs defaultValue="how-to" className="w-full px-3 pt-2">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="how-to" className="text-xs">How to Use</TabsTrigger>
              <TabsTrigger value="json" className="text-xs">JSON Schema</TabsTrigger>
              <TabsTrigger value="ai-prompt" className="text-xs">AI Prompt</TabsTrigger>
              <TabsTrigger value="examples" className="text-xs">Examples</TabsTrigger>
              <TabsTrigger value="plan" className="text-xs">Plan Outline</TabsTrigger>
            </TabsList>

            <ScrollArea className="h-[50vh] mt-3 pb-3">
              {/* How to Use */}
              <TabsContent value="how-to" className="mt-0 space-y-4 pr-4">
                <HowToTab />
              </TabsContent>

              {/* JSON Schema */}
              <TabsContent value="json" className="mt-0 space-y-4 pr-4">
                <JsonSchemaTab />
              </TabsContent>

              {/* AI Prompt */}
              <TabsContent value="ai-prompt" className="mt-0 space-y-5 pr-4">
                <AiPromptTab systemPrompt={ENHANCED_PROMPT} multiLayerExample={MULTI_LAYER_EXAMPLE} />
              </TabsContent>

              {/* Example Prompts */}
              <TabsContent value="examples" className="mt-0 space-y-4 pr-4">
                <ExamplesTab />
              </TabsContent>

              {/* Plan Outline */}
              <TabsContent value="plan" className="mt-0 space-y-4 pr-4">
                <PlanOutlineTabContent />
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </div>
      )}
    </div>
  );
}
